import { useEffect, useRef, useState } from "react";
import type { WriteRejection } from "@estelle/domain";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { describeRejection } from "@/modules/tasks/writes";

/**
 * The task description (#626): read as preformatted text, edited in place.
 *
 * Markdown, stored as written (`Task.description` in @estelle/domain) —
 * rendered as preformatted text rather than through a Markdown renderer: no
 * such dependency is installed, so showing exactly what was saved is the
 * honest option, not a half-parsed one.
 *
 * Click the text (or the empty-state line) to edit; Save writes through
 * `onSave`, Cancel or Esc throws the draft away, Ctrl/Cmd+Enter saves. A
 * rejected write leaves the editor open with the draft intact and the reason
 * beneath it. Without `canEdit` it is just the text — no affordance.
 */
export function DescriptionEditor({
  description,
  canEdit,
  onSave,
}: {
  description: string | null;
  canEdit: boolean;
  onSave: (description: string | null) => Promise<WriteRejection | null>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [pending, setPending] = useState(false);
  const [rejection, setRejection] = useState<WriteRejection | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!editing) return;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [editing]);

  /*
    Escape cancels the edit and nothing else. The task opens in a drawer, and
    the drawer listens for Escape on the document before the box could see it,
    so a handler on the box would discard the draft and close the whole drawer.
    This one sits on the window, ahead of the document, and stops the key
    there — only while the box has focus, so Escape elsewhere still closes the
    drawer.
  */
  useEffect(() => {
    if (!editing) return;
    const cancelEdit = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (document.activeElement !== textareaRef.current) return;
      event.stopPropagation();
      setEditing(false);
      setRejection(null);
    };
    window.addEventListener("keydown", cancelEdit, true);
    return () => {
      window.removeEventListener("keydown", cancelEdit, true);
    };
  }, [editing]);

  function startEditing() {
    setDraft(description ?? "");
    setRejection(null);
    setEditing(true);
  }

  function cancel() {
    setEditing(false);
    setRejection(null);
  }

  async function save() {
    if (pending) return;
    // An empty box clears the description; anything else is kept as typed.
    const next = draft.trim() === "" ? null : draft;
    if (next === (description ?? null)) {
      cancel();
      return;
    }
    setPending(true);
    try {
      const result = await onSave(next);
      if (result === null) {
        setEditing(false);
        setRejection(null);
      } else {
        setRejection(result);
      }
    } catch {
      // The request itself failed (offline, server down), not a refusal.
      setRejection({
        kind: "blocked",
        message: "Could not save the description. Try again.",
      });
    } finally {
      setPending(false);
    }
  }

  if (editing) {
    return (
      <div className="space-y-2">
        <Textarea
          ref={textareaRef}
          aria-label="Description"
          value={draft}
          readOnly={pending}
          rows={8}
          className="min-h-40 w-full font-sans"
          onChange={(event) => {
            setDraft(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
              event.preventDefault();
              void save();
            }
          }}
        />
        {rejection ? (
          <p className="text-destructive text-sm">
            {describeRejection(rejection)}
          </p>
        ) : null}
        <div className="flex gap-2">
          <Button
            className="h-11 flex-1 sm:h-9 sm:flex-none"
            disabled={pending}
            onClick={() => {
              void save();
            }}
          >
            Save
          </Button>
          <Button
            variant="outline"
            className="h-11 flex-1 sm:h-9 sm:flex-none"
            disabled={pending}
            onClick={cancel}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  const editProps = canEdit
    ? {
        role: "button",
        tabIndex: 0,
        "aria-label": "Edit description",
        onClick: startEditing,
        onKeyDown: (event: React.KeyboardEvent) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            startEditing();
          }
        },
      }
    : {};

  return description ? (
    <pre
      {...editProps}
      className={`bg-muted/30 rounded-md border p-3 font-sans text-sm break-words whitespace-pre-wrap ${
        canEdit ? "hover:bg-muted/50 cursor-text" : ""
      }`}
    >
      {description}
    </pre>
  ) : (
    <p
      {...editProps}
      className={`text-muted-foreground text-sm ${
        canEdit ? "hover:bg-muted/50 -mx-1 cursor-text rounded px-1 py-2" : ""
      }`}
    >
      {canEdit ? "No description. Click to add one." : "No description."}
    </p>
  );
}
